// AI おすすめ書籍画面（リスト追加 + 学べる内容 + フィードバック再提案）
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderSuggestionCard,
  renderHistorySection,
  renderBookFilter,
  getFilterState,
  applyBookFilter,
  bindFilterCounter,
} from '../ui.js';
import { recommendBooks, fetchAllNotesParallel } from '../api.js';
import {
  getAllBooks,
  upsertBooks,
  saveAiHistory,
  updateAiHistory,
  getAiHistoryByType,
  deleteAiHistory,
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
  wishlistId,
} from '../cache.js';

async function ensureNotes(books) {
  const missing = books.filter((b) => !b.note).length;
  if (!missing) return;
  showLoading(`書籍メモを補完中… (0/${missing})`);
  await fetchAllNotesParallel(books, (done, total) => {
    const ov = document.getElementById('overlay-msg');
    if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
  });
  await upsertBooks(books);
  hideLoading();
}

// 古い履歴（iterations を持たない）を新形式に正規化
function normalizePayload(payload) {
  if (!payload) return { items: [], iterations: [] };
  if (Array.isArray(payload.iterations) && payload.iterations.length) {
    const latest = payload.iterations[payload.iterations.length - 1];
    return { items: latest.items || payload.items || [], iterations: payload.iterations };
  }
  const items = payload.items || [];
  return {
    items,
    iterations: [{ items, feedback: null, ts: payload.createdAt || Date.now() }],
  };
}

// 過去 recommend 履歴から提案済み書籍を新しい順で重複除去して集める。
// Worker 側 formatExcludedBooks が上限 30 件で切るため、フロントでも 30 件で打ち切る。
async function collectPreviousRecommendItems(limit = 30) {
  const history = await getAiHistoryByType('recommend');
  const seen = new Set();
  const collected = [];
  for (const entry of history) {
    const payload = entry.payload || {};
    const iterations = Array.isArray(payload.iterations) && payload.iterations.length
      ? payload.iterations
      : [{ items: payload.items || [] }];
    for (const iter of iterations) {
      for (const it of iter.items || []) {
        const title = (it?.title || '').trim();
        const author = (it?.author || '').trim();
        if (!title) continue;
        const key = `${title}::${author}`;
        if (seen.has(key)) continue;
        seen.add(key);
        collected.push({ title, author });
        if (collected.length >= limit) return collected;
      }
    }
  }
  return collected;
}

export async function showRecommendScreen() {
  const books = await getAllBooks();
  if (!books.length) {
    showScreen('recommend', `${screenHead('おすすめの本')}<p class="empty">先に Notion 同期を実行してください。</p>`);
    document.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }
  await renderPicker(books);
}

async function renderPicker(books) {
  const histItems = await getAiHistoryByType('recommend');
  const html = `
    ${screenHead('おすすめの本')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      これまでの ${books.length} 冊の読書履歴とメモから、AI が次に読むべき日本語書籍を提案します。
    </p>
    ${renderBookFilter()}
    <div class="form-group">
      <label class="form-label">提案する冊数</label>
      <select class="form-select" id="recommend-count">
        <option value="3">3 冊</option>
        <option value="5" selected>5 冊</option>
        <option value="8">8 冊</option>
      </select>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>提案を生成</span>
    </button>
    <p style="margin-top:16px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1回あたり 約3-8円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('recommend', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  bindFilterCounter(screen, books);
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const count = parseInt(screen.querySelector('#recommend-count').value, 10);
    const filtered = applyBookFilter(books, getFilterState(screen));
    if (filtered.length < 2) {
      toast('絞り込み結果が少なすぎます（2冊以上必要）');
      return;
    }
    await runRecommend(filtered, count);
  });
  bindHistoryHandlers(screen, histItems, books);
}

function bindHistoryHandlers(screen, items, books) {
  screen.querySelectorAll('[data-hist-id]').forEach((row) => {
    const id = parseInt(row.dataset.histId, 10);
    row.addEventListener('click', async (e) => {
      if (e.target.closest('[data-hist-delete]')) return;
      const entry = items.find((x) => x.id === id);
      if (!entry) return;
      const payload = normalizePayload(entry.payload);
      const viewIndex = payload.iterations.length - 1;
      await renderResult({
        payload,
        books,
        count: entry.context?.count || 5,
        historyId: entry.id,
        viewIndex,
      });
    });
    row.querySelector('[data-hist-delete]')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm('この履歴を削除しますか？')) return;
      await deleteAiHistory(id);
      toast('削除しました');
      renderPicker(books);
    });
  });
}

async function runRecommend(books, count) {
  await ensureNotes(books);
  const previousItems = await collectPreviousRecommendItems();
  showLoading('AI が嗜好を分析中…');
  let result;
  try {
    result = await recommendBooks(books, count, { previousItems });
  } catch (e) {
    hideLoading();
    toast('提案の生成に失敗しました');
    return;
  } finally {
    hideLoading();
  }
  const items = result.items || [];
  const payload = {
    items,
    iterations: [{ items, feedback: null, ts: Date.now() }],
  };
  const historyId = await saveAiHistory({
    type: 'recommend',
    title: `おすすめ ${items.length} 冊`,
    subtitle: items.slice(0, 2).map((x) => x.title).join(' / '),
    context: { count, booksCount: books.length },
    payload,
  });
  await renderResult({ payload, books, count, historyId, viewIndex: 0 });
}

export async function renderFromHistory(entry) {
  const books = await getAllBooks();
  const payload = normalizePayload(entry.payload);
  const viewIndex = payload.iterations.length - 1;
  await renderResult({
    payload,
    books,
    count: entry.context?.count || 5,
    historyId: entry.id,
    viewIndex,
  });
}

async function renderResult({ payload, books, count, historyId, viewIndex }) {
  const iterations = payload.iterations || [];
  const latestIndex = iterations.length - 1;
  const safeIndex = Math.max(0, Math.min(viewIndex, latestIndex));
  const view = iterations[safeIndex] || { items: [], feedback: null };
  const items = view.items || [];
  const isLatest = safeIndex === latestIndex;
  const augmented = await Promise.all(
    items.map(async (item) => {
      const id = wishlistId(item.title, item.author);
      return { item, id, inList: await isInWishlist(id) };
    })
  );

  const tabsHtml =
    iterations.length > 1
      ? `<div class="iter-tabs" role="tablist" style="display:flex;flex-wrap:wrap;gap:6px;margin:0 0 18px;">
          ${iterations
            .map((it, idx) => {
              const label = idx === 0 ? '初回の提案' : `修正版 ${idx}`;
              const active = idx === safeIndex;
              return `<button type="button" class="chip ${active ? 'chip--ink' : ''}" data-iter-index="${idx}" aria-pressed="${active}" style="cursor:pointer;">${escapeHtml(label)}</button>`;
            })
            .join('')}
        </div>`
      : '';

  const feedbackTrace =
    view.feedback
      ? `<div style="margin:0 0 18px;padding:12px 14px;border:1px solid var(--line);">
          <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">your feedback</p>
          <p style="margin:0;font-family:var(--font-serif);font-size:14px;line-height:1.7;">${escapeHtml(view.feedback)}</p>
        </div>`
      : '';

  const refinePanel = isLatest
    ? `<section style="margin-top:32px;padding-top:24px;border-top:1px solid var(--line);">
        <p class="section-label" style="margin:0 0 12px;">refine this suggestion</p>
        <p style="color:var(--dim);font-size:13px;line-height:1.7;margin:0 0 12px;">
          この一覧に対して意見はありますか？ もっと別の角度で提案して欲しい場合は、ここに書いてください。AI が「初回の提案では満たせなかった軸」を踏まえて再提案します。
        </p>
        <div class="form-group">
          <textarea class="form-textarea" id="refine-feedback" rows="3" placeholder="例: もっと哲学・思想寄りの本が読みたい / 入門書ではなく原典に近いものを / 別の時代・地域の視点で"></textarea>
        </div>
        <button class="btn btn--ink btn--block" id="btn-refine" type="button">
          <svg class="icon"><use href="#i-sparkles"/></svg>
          <span>このフィードバックで再提案</span>
        </button>
      </section>`
    : `<button class="btn btn--block btn--ghost" id="btn-back-latest" type="button" style="margin-top:24px;">
        <svg class="icon"><use href="#i-refresh"/></svg>
        <span>最新の提案に戻る</span>
      </button>`;

  const html = `
    ${screenHead('おすすめの本', { right: '<button class="btn btn--icon" data-action="wishlist" type="button" aria-label="購入予定リスト"><svg class="icon"><use href="#i-bookmark"/></svg></button>' })}
    ${tabsHtml}
    ${feedbackTrace}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
      ${items.length} 冊を選びました。気になる本は「購入予定リストに加える」で保存できます。
    </p>
    ${augmented.map(({ item, id, inList }) => renderSuggestionCard(item, { id, isInWishlist: inList })).join('')}
    ${refinePanel}
  `;
  const screen = showScreen('recommend-result', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('[data-action="wishlist"]').addEventListener('click', () => {
    import('./wishlist.js').then((m) => m.showWishlistScreen());
  });

  // イテレーション切替
  screen.querySelectorAll('[data-iter-index]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.iterIndex, 10);
      if (idx === safeIndex) return;
      renderResult({ payload, books, count, historyId, viewIndex: idx });
    });
  });

  // 最新に戻る
  screen.querySelector('#btn-back-latest')?.addEventListener('click', () => {
    renderResult({ payload, books, count, historyId, viewIndex: latestIndex });
  });

  // 再提案
  screen.querySelector('#btn-refine')?.addEventListener('click', async () => {
    const feedback = screen.querySelector('#refine-feedback').value.trim();
    if (!feedback) {
      toast('意見を入力してください');
      return;
    }
    await runRefine({ payload, books, count, historyId, feedback });
  });

  // 各カードのリスト追加トグル
  screen.querySelectorAll('[data-suggestion-id]').forEach((card) => {
    const id = card.dataset.suggestionId;
    card.querySelector('[data-wishlist-toggle]').addEventListener('click', async (e) => {
      e.stopPropagation();
      const found = augmented.find((x) => x.id === id);
      if (!found) return;
      if (found.inList) {
        await removeFromWishlist(id);
        found.inList = false;
        toast('リストから削除しました');
      } else {
        await addToWishlist({ id, ...found.item, sourceScreen: 'recommend' });
        found.inList = true;
        toast('購入予定リストに追加');
      }
      const btn = card.querySelector('[data-wishlist-toggle]');
      btn.className = `btn btn--small btn--block ${found.inList ? '' : 'btn--ink'}`;
      btn.innerHTML = `<svg class="icon"><use href="#${found.inList ? 'i-check' : 'i-bookmark'}"/></svg><span>${found.inList ? '購入予定リストに追加済み（解除する）' : '購入予定リストに加える'}</span>`;
    });
  });
}

async function runRefine({ payload, books, count, historyId, feedback }) {
  // これまで提案された全書籍をまとめて除外リスト化
  const previousItems = [];
  for (const it of payload.iterations || []) {
    for (const x of it.items || []) {
      previousItems.push({ title: x.title, author: x.author });
    }
  }
  showLoading('AI が嗜好を再分析中…');
  let result;
  try {
    result = await recommendBooks(books, count, { feedback, previousItems });
  } catch (e) {
    hideLoading();
    toast('再提案に失敗しました');
    return;
  } finally {
    hideLoading();
  }
  const newItems = result.items || [];
  const newIterations = [
    ...payload.iterations,
    { items: newItems, feedback, ts: Date.now() },
  ];
  const newPayload = { items: newItems, iterations: newIterations };
  if (historyId) {
    const subtitle = newItems.slice(0, 2).map((x) => x.title).join(' / ');
    await updateAiHistory(historyId, {
      title: `おすすめ ${newItems.length} 冊（修正版 ${newIterations.length - 1}）`,
      subtitle,
      payload: newPayload,
    });
  }
  toast('再提案しました');
  await renderResult({
    payload: newPayload,
    books,
    count,
    historyId,
    viewIndex: newIterations.length - 1,
  });
}
